import { effect, inject, Injectable, OnDestroy, Signal, signal, computed } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { User_request } from '../../utils/interfaces';
import { DataService } from './data.service';
import { AuthService } from './auth.service';
import { PocketbaseService } from './pocketbase.service';
import { UserRequestsService } from './user-requests.service';

@Injectable({
  providedIn: 'root'
})
export class SidebarService implements OnDestroy {
  private readonly userRequestsService = inject(UserRequestsService);
  private readonly pocketbaseService = inject(PocketbaseService);

  // Viewport + preference based open-state management
  readonly #isSmallViewport = signal<boolean>(false);
  readonly #openPreference = signal<boolean>(true);
  public readonly isSideBarCollapsed = computed<boolean>(() => !this.#openPreference());

  readonly #userRequest = signal<User_request | null>(null);
  readonly userRequest = this.#userRequest.asReadonly() as unknown as Signal<User_request>;

  private readonly _activeRequestId = signal<string | null>(null);
  public readonly activeRequestId = this._activeRequestId.asReadonly();

  private subscribedReqId = '';
  public requests: User_request[] = [];
  // expose a reactive signal for requests
  readonly #requestsSignal = signal<User_request[]>([]);
  public readonly requestsSignal = this.#requestsSignal.asReadonly();
  // loading & error states
  readonly #isLoading = signal<boolean>(false);
  readonly #errorMessage = signal<string | null>(null);
  public readonly isLoading = this.#isLoading.asReadonly();
  public readonly errorMessage = this.#errorMessage.asReadonly();
  private isUpdating = false;

  public readonly isSmallViewport = this.#isSmallViewport.asReadonly();

  private mediaQueryList?: MediaQueryList;
  private mediaQueryListener?: (e: MediaQueryListEvent) => void;

  // Public methods for testing purposes
  public setViewportForTesting(isSmall: boolean): void {
    this.#isSmallViewport.set(isSmall);
  }

  public setPreferenceForTesting(isOpen: boolean): void {
    this.#openPreference.set(isOpen);
  }

  constructor(
    private cookieService: CookieService,
    private dataService: DataService,
    private authService: AuthService
  ) {
    effect(() => {
      if (!this.authService.isAuthenticated()) {
        this.reset();
      }
    }, { allowSignalWrites: true });

    // Initialize user preference from cookie (defaults to true/open on large screens)
    const cookieValue = this.cookieService.get('sidebar-toggle');
    this.#openPreference.set(cookieValue === 'true' || cookieValue === '');

    // Setup viewport breakpoint listener
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      const mq = window.matchMedia('(max-width: 992px)'); // Bootstrap lg breakpoint
      this.mediaQueryList = mq;
      this.#isSmallViewport.set(mq.matches);
      this.mediaQueryListener = (e: MediaQueryListEvent) => {
        this.#isSmallViewport.set(e.matches);
      };
      // Support older browsers
      if (mq.addEventListener) {
        mq.addEventListener('change', this.mediaQueryListener);
      } else if ((mq as any).addListener) {
        (mq as any).addListener(this.mediaQueryListener);
      }
    }
  }

  setActiveRequestId(id: string | null): void {
    this._activeRequestId.set(id);
  }

  toggle() {
    // Update user preference; effective open state also depends on viewport size
    this.#openPreference.set(!this.#openPreference());
    this.cookieService.set('sidebar-toggle', this.#openPreference().toString());
  }

  async updateLoadedRequest(requestId: string) {
    const freshRequest = await this.dataService.loadUsersReuqestById(requestId);
    if (!freshRequest) return;


    // Protection: If we are already 90 (Finished), don't regress to <90.
    const current = this.#userRequest();
    if (current?.status === 90 && freshRequest.status && freshRequest.status < 90) {
      console.warn('Ignoring stale backend status', freshRequest.status, 'keeping local 90');
      freshRequest.status = 90;
      // Preserve votum if we have it
      if (current.votum && !freshRequest.votum) {
        freshRequest.votum = current.votum;
      }
    }

    this.#userRequest.set(freshRequest);
    this.userRequestsService.setUserRequest(freshRequest as any);
  }

  async subscribeToRequest(requestID: string) {
    if (this.subscribedReqId == requestID) return;
    this.subscribedReqId = requestID;
    this.pocketbaseService.getPocketBase().collection('requests').subscribe(requestID, async (e) => {
      if (e.action == 'update') {
        const isActiveRequest = this.userRequest()?.id === requestID;
        if (isActiveRequest) {
          const currentStatus = this.userRequest()?.status;
          // Prevent regression: If we are already 90 (Finished), don't process 89 (Generating) updates.
          // This avoids race conditions where a delayed "89" event overwrites our optimistic "90" state.
          if (currentStatus === 90 && e.record['status'] < 90) {
            return;
          }
          await this.updateLoadedRequest(requestID);
        } else {
          this.updateCachedRequestFromEvent(e.record);
        }
      }
      // Status 90 is the new "Finished" state for Votum generation.
      // 88 might be intermediate or legacy. 89 is Generating.
      // We assume completion when we hit 90.
      if (e.record['status'] == 90) {
        await this.updateRequests();
        await this.pocketbaseService.getPocketBase().collection('requests').unsubscribe(requestID);
      }
    }, { /* other options like expand, custom headers, etc. */ });
  }

  private updateCachedRequestFromEvent(record: any) {
    const idx = this.requests.findIndex(req => req.id === record?.id);
    if (idx === -1) {
      return;
    }

    const current = this.requests[idx];

    // Protection: If current is 90, ignore regressions
    let newStatus = record?.status ?? current.status;
    if (current.status === 90 && newStatus < 90) {
      newStatus = 90;
    }

    const patched = {
      ...current,
      status: newStatus,
      updated: record?.updated ?? current.updated,
      display_name: record?.display_name ?? current.display_name,
      generate_votum: record?.generate_votum ?? current.generate_votum,
      votum: record?.votum ?? current.votum
    };

    this.requests[idx] = patched;
    this.#requestsSignal.set([...this.requests]);
  }

  setLoadedRequest(request: User_request) {
    this.#userRequest.set(request);
    this.subscribeToRequest(request.id);
    this.userRequestsService.setUserRequest(request as any);
  }

  async updateRequests() {
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;
    this.#isLoading.set(true);
    this.#errorMessage.set(null);
    try {
      const loadedRequests = await this.dataService.loadUsersReuqests();
      this.requests = loadedRequests || [];
      this.#requestsSignal.set(this.requests);
    } catch (error: any) {
      // On 401/network errors at startup the auth token may not have refreshed yet.
      // Retry once after a short delay before showing the error.
      const status = error?.status ?? error?.response?.status;
      if (status === 401 || status === 0 || status == null) {
        try {
          await new Promise(resolve => setTimeout(resolve, 800));
          const retried = await this.dataService.loadUsersReuqests();
          this.requests = retried || [];
          this.#requestsSignal.set(this.requests);
          return;
        } catch (retryError) {
          console.error("Retry failed in SidebarService:", retryError);
        }
      } else {
        console.error("Failed to update requests in SidebarService:", error);
      }
      this.requests = [];
      this.#requestsSignal.set([]);
      this.#errorMessage.set('Fehler beim Laden der Anfragen.');
    } finally {
      this.isUpdating = false;
      this.#isLoading.set(false);
    }
  }

  ngOnDestroy() {
    if (this.subscribedReqId) {
      this.pocketbaseService.getPocketBase().collection('requests').unsubscribe(this.subscribedReqId); // Corrected collection
    }
    if (this.mediaQueryList && this.mediaQueryListener) {
      if (this.mediaQueryList.removeEventListener) {
        this.mediaQueryList.removeEventListener('change', this.mediaQueryListener);
      } else if ((this.mediaQueryList as any).removeListener) {
        (this.mediaQueryList as any).removeListener(this.mediaQueryListener);
      }
    }
  }

  // Reset all state on logout
  reset(): void {
    this.#userRequest.set(null);
    this.userRequestsService.setUserRequest(null);
    const oldSubscribedReqId = this.subscribedReqId; // Store before clearing
    this.subscribedReqId = '';
    this.requests = [];
    this.#requestsSignal.set([]);
    this.#isLoading.set(false);
    this.#errorMessage.set(null);

    // Unsubscribe from any active request subscriptions
    if (oldSubscribedReqId) {
      this.pocketbaseService.getPocketBase().collection('requests').unsubscribe(oldSubscribedReqId); // Corrected collection
    }
  }

}
